import type React from 'react';
import { useContext } from 'react';
import useDateTime from 'hooks/useDateTime';
import SocialShareDropdown from 'components/SocialShareDropdown';
import getArticlePageTestId from 'constants/testsIds/articlePage';
import { useOptimizely } from 'providers/OptimizelyProvider';
import type { HeroConfig } from 'interfaces/content/articles/Articles';
import AuthorsByline from 'components/AuthorsByline';
import * as S from './FeatureBylineStyles';
import { UserContext } from 'providers/UserProvider';
import RawHtml from 'utils/miscUtils/rawHtml';
import { GoogleLogo } from 'icons/GoogleLogo';
import { trackOptimizelyEvent } from 'providers/OptimizelyProvider';

export const FeatureByline: React.FC<HeroConfig> = ({ article, isFeatureArticle = false }) => {
  const { authors, dateGmt, modifiedGmt, showModifiedTimestamp } = article;
  const { isMobileApp } = useContext(UserContext);
  const { userFlags: userVariants } = useOptimizely();
  const showGoogleButton = (userVariants?.googleSourceButton as { variant: string })?.variant === 'on' ? true : false;

  const localDate = useDateTime(dateGmt);
  const updatedDate = useDateTime(modifiedGmt);
  const isUpdated = !(localDate === updatedDate);

  const trackingData = {
    contentPlacementCD: 'article_byline',
    eventAction: 'byline click',
    eventCategory: 'navigation',
  };

  const socialShareTrackingData = {
    contentPlacementCD: 'article_byline',
    eventAction: 'Social Share -',
    eventCategory: 'Social',
  };

  const isInSections = (section: string) => {
    if (article.sectionNames.includes(section)) {
      return true;
    }
    return false;
  };
  const isBylineCommentary =
    isInSections('Commentary') ||
    isInSections('Partner Commentary') ||
    article.premiumCategory.name === 'F-Partnership';

  const isFWire = article.premiumCategory.name === 'F-Wire';

  const openGooglePopup = () => {
    const url = 'https://www.google.com/preferences/source?q=fortune.com';
    const windowFeatures = 'menubar=no,toolbar=no,status=no,width=600,height=600,top=100,left=100';

    window.open(url, 'GooglePopup', windowFeatures);
  };

  return (
    <S.BylineAndShareWrapper
      isFeatureArticle={isFeatureArticle}
      isMobileApp={isMobileApp}
    >
      <S.RedesignedHeroBylineWrapper data-cy={getArticlePageTestId('AUTHOR_AND_DATE')}>
        <S.RedesignedHeroAuthorsContainer>
          <AuthorsByline
            authors={authors}
            reviewedBy={article.reviewedBy}
            trackingData={{ ...trackingData, eventLabel: 'author click' }}
            isFWire={isFWire}
            dataCy={getArticlePageTestId('AUTHORS')}
          />
        </S.RedesignedHeroAuthorsContainer>{' '}
        {isBylineCommentary && <RawHtml html={article.postSettings.bylineCommentary} />}
        <S.RedesignedHeroDate data-cy={getArticlePageTestId('PUBLISH_DATE')}>{localDate}</S.RedesignedHeroDate>
        {showModifiedTimestamp && isUpdated ? (
          <S.RedesignedHeroDate data-cy={getArticlePageTestId('UPDATED_DATE')}>
            Updated {updatedDate}
          </S.RedesignedHeroDate>
        ) : null}
      </S.RedesignedHeroBylineWrapper>
      <S.ShareWrapper>
        {!isMobileApp && (
          <SocialShareDropdown
            title={article.title}
            trackingData={socialShareTrackingData}
          />
        )}
        {showGoogleButton && (
          <S.GooglePromptWrapper
            onClick={() => {
              trackOptimizelyEvent('addOnGoogleClicked', {});
              openGooglePopup();
            }}
          >
            <S.GooglePromptTitle>Add us on</S.GooglePromptTitle>
            <GoogleLogo />
          </S.GooglePromptWrapper>
        )}
      </S.ShareWrapper>
    </S.BylineAndShareWrapper>
  );
};

export default FeatureByline;
